import PropTypes from "prop-types";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { apiErrorMessage } from "../../services/apiError";
import { formatInstant } from "./lib/format";

/**
 * "Robinhood approval: ON as of <time>" — the in-app trade approval as the
 * latest snapshot read it (spec §2.1: the security boundary for money; D12:
 * a sync plan needs it on). OFF reads loudly, as an alert: nothing can
 * trade while it's off. Before the first snapshot it says when it's read.
 *
 * `latest` is useLatestSnapshot's state.
 */
function ApprovalReading({ latest }) {
  const { loading, snapshot, error, retry } = latest;
  if (error) {
    return (
      <Alert variant="destructive" role="alert" aria-label="Robinhood approval" className="mt-3">
        <AlertDescription className="flex flex-wrap items-center gap-2">
          <span>Couldn&apos;t read the latest snapshot: {apiErrorMessage(error, "it didn't load.")}</span>{" "}
          <Button type="button" variant="outline" size="sm" className="h-11 md:h-8" onClick={retry}>
            Retry
          </Button>
        </AlertDescription>
      </Alert>
    );
  }
  if (loading && !snapshot) {
    return <p className="mt-3 mb-0 text-sm text-muted-foreground">Reading the latest snapshot…</p>;
  }
  if (!snapshot) {
    return (
      <p className="mt-3 mb-0 text-sm">
        Robinhood approval: not read yet — the executor reads it with the account at its first sync.
      </p>
    );
  }
  const when = formatInstant(snapshot.broker_approval_read_at);
  const asOf = when ? ` as of ${when}` : " — when it was read isn't known";
  if (snapshot.broker_approval_on) {
    return (
      <p className="mt-3 mb-0 text-sm font-medium">
        Robinhood approval: ON{asOf}
      </p>
    );
  }
  return (
    <Alert variant="destructive" role="alert" aria-label="Robinhood approval" className="mt-3 border-destructive">
      <AlertDescription>
        <p className="m-0 font-semibold">
          Robinhood approval: OFF{asOf}
        </p>
        <p className="m-0">
          A sync plan is refused while it&apos;s off, so nothing trades. Turn on trade approval for the “MCP”
          account in the Robinhood app; the next snapshot reads it again.
        </p>
      </AlertDescription>
    </Alert>
  );
}

ApprovalReading.propTypes = {
  latest: PropTypes.shape({
    loading: PropTypes.bool.isRequired,
    snapshot: PropTypes.shape({
      broker_approval_on: PropTypes.bool.isRequired,
      broker_approval_read_at: PropTypes.string,
    }),
    error: PropTypes.object,
    retry: PropTypes.func.isRequired,
  }).isRequired,
};

export default ApprovalReading;
